import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ModalService } from '@pkg-services/modal/modal.service';
import { ActionButtonComponent } from '@pkg-components/button/action-button/action-button.component';
import { ModalHostComponent } from './modal-host.component';

interface ModalInstance {
  id: number;
  title?: string;
  component: any;
  data?: Record<string, unknown>;
  hideCloseBt?: boolean;
  hasStickyFooter?: boolean;
  isCentered?: boolean;
  contentMaxWidth?: string;
  presentation?: 'from-left' | 'from-right' | 'from-top' | 'from-bottom' | string;
  /** Class d'animation courante (from-bottom, modal-in-from-bottom, modal-out-from-bottom) */
  animationClass?: string;
  callback?: (result?: unknown) => void;
  callbackClose?: (result?: unknown) => void;
}

@Component({
  selector: 'app-modal-container',
  standalone: true,
  imports: [CommonModule, ActionButtonComponent, ModalHostComponent],
  templateUrl: './modal-container.component.html',
  styleUrl: './modal-container.component.scss'
})
export class ModalContainerComponent implements OnInit, OnDestroy {
  private modalService = inject(ModalService);
  private sub?: Subscription;

  modals: ModalInstance[] = [];

  get topModal(): ModalInstance | undefined {
    return this.modals.length > 0 ? this.modals[this.modals.length - 1] : undefined;
  }

  ngOnInit(): void {
    this.sub = this.modalService.getObservable().subscribe((params: any) => {
      if (params?.destroy === true) {
        this.modals = [];
        return;
      }
      if (params?.id && params?.component) {
        const presentation = params.presentation || 'from-bottom';
        const entry: ModalInstance = {
          id: params.id,
          title: params.title,
          component: params.component,
          data: params.data,
          hideCloseBt: params.hideCloseBt,
          hasStickyFooter: params.hasStickyFooter !== false,
          isCentered: params.isCentered,
          contentMaxWidth: params.contentMaxWidth,
          presentation,
          animationClass: `modal-in-${presentation}`,
          callback: params.callback,
          callbackClose: params.callbackClose
        };
        this.modals.push(entry);
        setTimeout(() => {
          const m = this.modals.find(modal => modal.id === entry.id);
          if (m) m.animationClass = 'modal-in-' + presentation;
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  removeModal(id: number, result?: unknown): void {
    const modal = this.modals.find(m => m.id === id);
    if (!modal) return;
    const presentation = modal.presentation || 'from-bottom';
    modal.animationClass = 'modal-out-' + presentation;
    setTimeout(() => {
      if (modal?.callbackClose) modal.callbackClose(result);
      this.modals = this.modals.filter(m => m.id !== id);
    }, 300);
  }

  getModalData(modal: ModalInstance): Record<string, unknown> {
    return {
      ...(modal.data || {}),
      close: (result?: unknown) => this.removeModal(modal.id, result)
    };
  }
}
